import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { VariantContextData } from "../VariantBuilderContext";
import unwrapBundlerHtml from "../../../utils/unwrapBundlerHtml";

const looksLikeCsv = (text) => {
  const lines = text.trim().split("\n");
  return lines.length > 1 && lines.every((line) => line.split(",").length > 1);
};

const AttachMenuChevron = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    className="text-black dark:text-white ml-auto flex-shrink-0"
  >
    <path
      d="M9 6L15 12L9 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MENU_ROW_CLASS =
  "w-full border-0 bg-transparent text-left px-4 py-3 text-xl text-gray-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer flex items-center gap-2 whitespace-nowrap";

// Round "+" button with the Remix-chat-style upload picker (image / HTML /
// MD / CSV). Calls onAttach with { type, url?, content?, name }.
const AttachmentUploader = ({ onAttach, onUploadBusy, onUploadError }) => {
  const uploadsInTransit = useRef(0);
  const busy = (delta) => {
    if (typeof onUploadBusy === "function") onUploadBusy(delta);
  };
  const [contextData] = useContext(VariantContextData);
  const [menuOpen, setMenuOpen] = useState(false);
  // fixed-viewport coords so the menu can be portaled out of the composer,
  // whose overflow-hidden (for rounded corners) would otherwise clip it
  const [menuPosition, setMenuPosition] = useState(null);
  const fileInput = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const website = contextData.objects.website;

  const openMenu = () => {
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPosition({
      bottom: window.innerHeight - rect.top + 8,
      left: rect.left,
    });
    setMenuOpen(true);
  };

  const toggleMenu = () => (menuOpen ? setMenuOpen(false) : openMenu());

  // dismiss on outside click and on scroll/resize (the fixed menu can't follow
  // the button, so close rather than let it drift)
  useEffect(() => {
    if (!menuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      if (buttonRef.current && buttonRef.current.contains(event.target)) return;
      setMenuOpen(false);
    };
    const close = () => setMenuOpen(false);

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [menuOpen]);

  const handleImageUpload = () => {
    setMenuOpen(false);
    if (typeof uploads === "undefined" || !uploads.select) return;

    uploads.select("callback", {
      environment: contextData.environment,
      path: "/websites/" + website.id + "/",
      website_id: website.id,
      library: true,
      onUploadStarted: () => {
        uploadsInTransit.current += 1;
        busy(1);
      },
      onUploadFinished: () => {
        uploadsInTransit.current = Math.max(0, uploadsInTransit.current - 1);
      },
      onUploadFailed: (reason) => {
        busy(-1);
        if (typeof onUploadError === "function") onUploadError(reason);
      },
      onClose: () => {
        if (uploadsInTransit.current > 0) busy(-uploadsInTransit.current);
        uploadsInTransit.current = 0;
      },
      callback: (file_url, file_meta) => {
        busy(-1);
        const originalName =
          file_meta && (file_meta.filename || file_meta.cf_original_filename);
        onAttach({
          type: "image",
          url: file_url,
          name: originalName || file_url.split("/").pop(),
        });
      },
    });
  };

  const openFilePicker = (accept) => {
    setMenuOpen(false);
    if (!fileInput.current) return;

    fileInput.current.setAttribute("accept", accept);
    fileInput.current.click();
  };

  const handleFileUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    setMenuOpen(false);
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || "");
      const isMarkdown = /\.(md|markdown)$/i.test(file.name);
      const isCsv =
        !isMarkdown && (/\.csv$/i.test(file.name) || looksLikeCsv(raw));
      const content = isMarkdown || isCsv ? raw : unwrapBundlerHtml(raw);
      onAttach({
        type: isMarkdown ? "markdown" : isCsv ? "csv" : "html",
        content: content,
        name: file.name,
      });
    };
    reader.readAsText(file);
  };

  return (
    <div className="relative flex items-center shrink-0">
      <button
        ref={buttonRef}
        className="w-[30px] h-[30px] shrink-0 flex items-center justify-center rounded-full border border-solid border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-600 dark:text-slate-200 cursor-pointer hover:bg-gray-50 hover:border-gray-400 dark:hover:bg-slate-800 dark:hover:border-slate-500 transition-colors p-0"
        style={{ minWidth: "30px", minHeight: "30px" }}
        title="Add an attachment"
        onClick={toggleMenu}
      >
        <span
          className="text-3xl leading-none font-light"
          style={{ marginTop: "-2px" }}
        >
          +
        </span>
      </button>
      {menuOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[100000] w-64 bg-white dark:bg-slate-900 border border-solid border-transparent dark:border-slate-700 rounded-xl shadow-lg overflow-hidden"
            style={{ bottom: menuPosition.bottom, left: menuPosition.left }}
          >
            <button
              type="button"
              className={MENU_ROW_CLASS}
              onClick={handleImageUpload}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                className="text-black dark:text-white flex-shrink-0"
              >
                <rect
                  x="3"
                  y="5"
                  width="18"
                  height="14"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <circle cx="8.5" cy="9.5" r="1.5" fill="currentColor" />
                <path
                  d="M5.5 16L10 12L13 14.5L16.5 11L18.5 13V16"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Upload an image</span>
              <AttachMenuChevron />
            </button>
            <div className="h-px bg-slate-200 dark:bg-slate-700 mx-3" />
            <button
              type="button"
              className={MENU_ROW_CLASS}
              onClick={() => openFilePicker(".html,.htm,text/html")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                className="text-black dark:text-white flex-shrink-0"
              >
                <path
                  d="M14 2H7C5.9 2 5 2.9 5 4V20C5 21.1 5.9 22 7 22H17C18.1 22 19 21.1 19 20V7L14 2Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <path d="M14 2V7H19" stroke="currentColor" strokeWidth="1.8" />
                <path
                  d="M9 12H15"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
              <span>Upload HTML</span>
              <AttachMenuChevron />
            </button>
            <div className="h-px bg-slate-200 dark:bg-slate-700 mx-3" />
            <button
              type="button"
              className={MENU_ROW_CLASS}
              onClick={() => openFilePicker(".md,.markdown,text/markdown")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                className="text-black dark:text-white flex-shrink-0"
              >
                <rect
                  x="2.5"
                  y="5.5"
                  width="19"
                  height="13"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <path
                  d="M5.5 15V9L8 12L10.5 9V15"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16 9V14.5M16 14.5L13.75 12.25M16 14.5L18.25 12.25"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Upload MD</span>
              <AttachMenuChevron />
            </button>
            <div className="h-px bg-slate-200 dark:bg-slate-700 mx-3" />
            <button
              type="button"
              className={MENU_ROW_CLASS}
              onClick={() => openFilePicker(".csv,text/csv")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                className="text-black dark:text-white flex-shrink-0"
              >
                <rect
                  x="3"
                  y="4"
                  width="18"
                  height="16"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.8"
                />
                <path d="M3 9.5H21" stroke="currentColor" strokeWidth="1.8" />
                <path d="M3 14.5H21" stroke="currentColor" strokeWidth="1.8" />
                <path d="M9.5 4V20" stroke="currentColor" strokeWidth="1.8" />
              </svg>
              <span>Upload CSV</span>
              <AttachMenuChevron />
            </button>
          </div>,
          document.body
        )}
      <input
        ref={fileInput}
        type="file"
        accept=".csv,.html,.htm,.md,.markdown,text/csv,text/html,text/markdown"
        className="hidden"
        onChange={handleFileUpload}
      />
    </div>
  );
};

export default AttachmentUploader;
